import React, { memo } from "react";
import PropTypes from "prop-types";
import { CheckIcon, XMarkIcon, InformationCircleIcon } from "@heroicons/react/24/outline";

function MobileVersion({ featuresList }) {
  const getCellContent = row => {
    if (row.label === "Check") {
      return (
        <div className="flex items-center">
          <div className="flex size-5 items-center justify-center rounded-full bg-green-500/20 ring-2 ring-green-400/50">
            <CheckIcon className="size-3 text-green-400" />
          </div>
          <span className="ml-2 font-semibold text-green-400">Yes</span>
        </div>
      );
    }
    if (row.label === "Cancel") {
      return (
        <div className="flex items-center">
          <div className="flex size-5 items-center justify-center rounded-full bg-red-500/20 ring-2 ring-red-400/50">
            <XMarkIcon className="size-3 text-red-400" />
          </div>
          <span className="ml-2 font-semibold text-red-400">No</span>
        </div>
      );
    }
    return <span className="font-semibold text-gray-300">{row.label}</span>;
  };

  const getFundviewContent = row => {
    if (row.label === "Check") {
      return (
        <div className="flex items-center">
          <div className="flex size-6 items-center justify-center rounded-full bg-gradient-to-r from-blue-500 to-purple-500 shadow-lg">
            <CheckIcon className="size-3 text-white" />
          </div>
          <span className="ml-2 font-bold text-blue-400">Included</span>
        </div>
      );
    }
    return (
      <span className="inline-flex items-center rounded-full border border-blue-400/30 bg-gradient-to-r from-blue-500/20 to-purple-500/20 px-3 py-1 text-xs font-semibold text-blue-300 backdrop-blur-sm">
        {row.label}
      </span>
    );
  };

  return (
    <div className="mt-16 lg:hidden">
      <div className="space-y-10">
        {featuresList.map((category, categoryIndex) => (
          <div key={category.id} className="space-y-6">
            {/* Enhanced Category Header */}
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 p-8 shadow-2xl">
              {/* Background Effects */}
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600/90 via-purple-600/90 to-indigo-600/90" />
              <div className="absolute right-0 top-0 size-32 rounded-full bg-white/10 blur-2xl" />

              <div className="relative flex items-center space-x-4">
                <div className="shrink-0">
                  <div className="flex size-12 items-center justify-center rounded-xl border border-white/30 bg-white/20 shadow-xl backdrop-blur-sm">
                    <span className="text-lg font-bold text-white">{categoryIndex + 1}</span>
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">{category.category}</h3>
                </div>
              </div>
            </div>

            {/* Enhanced Features for this Category */}
            <div className="space-y-4">
              {category.features.map(feature => (
                <div
                  key={feature.id}
                  className="relative rounded-xl border border-gray-700/50 bg-gray-900/90 p-6 shadow-2xl backdrop-blur-xl"
                >
                  {/* Subtle background glow */}
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-purple-500/5" />

                  <div className="relative">
                    <div className="mb-6 flex items-start justify-between">
                      <h4 className="flex-1 text-lg font-bold leading-relaxed text-white">{feature.label}</h4>
                      {feature.popoverText && (
                        <div className="group relative ml-3">
                          <InformationCircleIcon className="size-5 cursor-help text-gray-500 transition-colors duration-200 hover:text-blue-400" />
                          <div className="pointer-events-none absolute bottom-full right-0 z-50 mb-3 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                            <div className="max-w-xs break-words rounded-lg border border-gray-600 bg-gray-800 px-4 py-3 text-xs text-gray-200 shadow-xl backdrop-blur-sm">
                              {feature.popoverText}
                              <div className="absolute right-4 top-full border-4 border-transparent border-t-gray-800" />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="space-y-4">
                      <div className="flex items-center justify-between border-b border-gray-700/50 py-3">
                        <span className="text-sm font-semibold uppercase tracking-wide text-gray-400">Basic CRM</span>
                        <div className="flex items-center text-right">
                          {getCellContent(feature.rows[1])}
                          {feature.rows[1].popoverText && (
                            <div className="group relative ml-2">
                              <InformationCircleIcon className="size-4 cursor-help text-gray-500 transition-colors duration-200 hover:text-blue-400" />
                              <div className="pointer-events-none absolute bottom-full right-0 z-50 mb-3 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                                <div className="max-w-xs break-words rounded-lg border border-gray-600 bg-gray-800 px-4 py-3 text-xs text-gray-200 shadow-xl backdrop-blur-sm">
                                  {feature.rows[1].popoverText}
                                  <div className="absolute right-4 top-full border-4 border-transparent border-t-gray-800" />
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center justify-between border-b border-gray-700/50 py-3">
                        <span className="text-sm font-semibold uppercase tracking-wide text-gray-400">
                          Premium CRM / CRE Software
                        </span>
                        <div className="flex items-center text-right">
                          {getCellContent(feature.rows[2])}
                          {feature.rows[2].popoverText && (
                            <div className="group relative ml-2">
                              <InformationCircleIcon className="size-4 cursor-help text-gray-500 transition-colors duration-200 hover:text-blue-400" />
                              <div className="pointer-events-none absolute bottom-full right-0 z-50 mb-3 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                                <div className="max-w-xs break-words rounded-lg border border-gray-600 bg-gray-800 px-4 py-3 text-xs text-gray-200 shadow-xl backdrop-blur-sm">
                                  {feature.rows[2].popoverText}
                                  <div className="absolute right-4 top-full border-4 border-transparent border-t-gray-800" />
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center justify-between rounded-xl border border-blue-400/20 bg-gradient-to-r from-blue-500/10 to-purple-500/10 p-4">
                        <span className="text-sm font-bold uppercase tracking-wide text-blue-300">Fundview</span>
                        <div className="text-right">{getFundviewContent(feature.rows[0])}</div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Total Value Section for Mobile */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-green-600 via-emerald-600 to-green-600 p-8 shadow-2xl">
          {/* Background Effects */}
          <div className="absolute inset-0 bg-gradient-to-r from-green-600/90 via-emerald-600/90 to-green-600/90" />
          <div className="absolute right-0 top-0 size-32 rounded-full bg-white/10 blur-2xl" />
          <div className="absolute bottom-0 left-0 size-24 rounded-full bg-white/5 blur-xl" />

          <div className="relative">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="shrink-0">
                  <div className="flex size-14 items-center justify-center rounded-xl border border-white/30 bg-white/20 shadow-xl backdrop-blur-sm">
                    <span className="text-xl font-bold text-white">∑</span>
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Total Monthly Value</h3>
                  <p className="text-sm text-green-100">Combined efficiency savings</p>
                </div>
              </div>
            </div>

            <div className="pt-4 text-center">
              <div className="mb-2 text-4xl font-bold text-white">$1,200/mo</div>
              <div className="text-sm text-green-100">Annual Value: $14,400</div>
              <div className="mt-4 border-t border-white/20 pt-4">
                <p className="text-xs leading-relaxed text-green-50">
                  These efficiency gains represent real time and cost savings that Fundview delivers through automation
                  and optimization.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

MobileVersion.propTypes = {
  featuresList: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      category: PropTypes.string.isRequired,
      features: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          label: PropTypes.string.isRequired,
          popoverText: PropTypes.string,
          rows: PropTypes.arrayOf(
            PropTypes.shape({
              id: PropTypes.string.isRequired,
              label: PropTypes.string.isRequired,
              popoverText: PropTypes.string,
            }),
          ).isRequired,
        }),
      ).isRequired,
    }),
  ).isRequired,
};

export default memo(MobileVersion);
